import { Link, useLocation } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

type DashboardSection =
	| "home"
	| "chat"
	| "api-keys"
	| "generations"
	| "billing"
	| "usage";

function getDashboardSectionFromPathname(
	pathname: string,
): DashboardSection | null {
	if (pathname === "/dashboard") return "home";
	if (pathname.startsWith("/dashboard/chat")) return "chat";
	if (pathname.startsWith("/dashboard/api-keys")) return "api-keys";
	if (pathname.startsWith("/dashboard/generations")) return "generations";
	if (pathname.startsWith("/dashboard/billing")) return "billing";
	if (pathname.startsWith("/dashboard/usage")) return "usage";
	return null;
}

interface DashboardBottomNavProps {
	className?: string;
}

function DashboardBottomNav({ className }: DashboardBottomNavProps) {
	const location = useLocation();
	const activeSection = getDashboardSectionFromPathname(location.pathname);

	const navItems = [
		{
			section: "home" as const,
			icon: "dashboard",
			label: "Home",
			path: "/dashboard",
		},
		{
			section: "chat" as const,
			icon: "forum",
			label: "Chat",
			path: "/dashboard/chat",
		},
		{
			section: "generations" as const,
			icon: "history",
			label: "Generations",
			path: "/dashboard/generations",
		},
		{
			section: "api-keys" as const,
			icon: "vpn_key",
			label: "API Keys",
			path: "/dashboard/api-keys",
		},
		{
			section: "usage" as const,
			icon: "monitoring",
			label: "Usage",
			path: "/dashboard/usage",
		},
		{
			section: "billing" as const,
			icon: "account_balance_wallet",
			label: "Billing",
			path: "/dashboard/billing",
		},
	];

	return (
		<nav
			aria-label="Mobile bottom navigation"
			className={cn(
				"fixed bottom-0 inset-x-0 h-16 backdrop-blur-xl border-t lg:hidden z-50",
				"pb-[env(safe-area-inset-bottom,0px)]",
				className,
			)}
			style={{
				backgroundColor: "rgba(10,10,10,0.95)",
				borderColor: "rgba(255,255,255,0.08)",
			}}
		>
			<div className="flex items-center justify-around h-full max-w-md mx-auto">
				{navItems.map((item) => {
					const isActive = activeSection === item.section;

					return (
						<Link
							key={item.section}
							to={item.path}
							aria-current={isActive ? "page" : undefined}
							className="flex flex-col items-center justify-center gap-1 flex-1 py-2 px-1 transition-colors relative"
							style={{ color: isActive ? "#6B3FF2" : "var(--km-on-surface-muted)" }}
						>
							<span
								className="material-symbols-outlined"
								aria-hidden="true"
								style={{
									fontSize: "20px",
									fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0",
								}}
							>
								{item.icon}
							</span>
							<span
								className="text-[10px] uppercase font-bold"
								style={{
									fontFamily: "inherit",
									letterSpacing: isActive ? "0.2em" : "0.1em",
								}}
							>
								{item.label}
							</span>
							{isActive && (
								<div
									className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5"
									style={{ backgroundColor: "#6B3FF2" }}
								/>
							)}
						</Link>
					);
				})}
			</div>
		</nav>
	);
}

export { DashboardBottomNav };
